import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createUseStyles } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';

import FieldControl from './FieldControl';

import Section from './types/Section';
import InputGroup from './types/InputGroup';
import MultiComponent from './types/MultiComponent';
import CheckboxGroup from './types/CheckboxGroup';
import Checkbox from './types/Checkbox';
import Switch from './types/Switch';
import RadioGroup from './types/RadioGroup';
import BooleanRadioGroup from './types/BooleanRadioGroup';
import List from './types/List';
import MultiList from './types/MultiList';
import Message from './types/Message';
import Static from './types/Static';
import Text from './types/Text';
import Textarea from './types/Textarea';
import HtmlTextarea from './types/HtmlTextarea';
import Integer from './types/Integer';
import NumberType from './types/Number';
import Date from './types/Date';
import Time from './types/Time';
import Datetime from './types/Datetime';
import File from './types/File';
import WidgetSize from './types/WidgetSize';
import ColorPicker from './types/ColorPicker';
import ImagePicker from './types/ImagePicker';
import Slider from './types/Slider';
import Code from './types/Code';

import getDefaults from './lib/getDefaults';

import styles from './styles/index';

export { default as useForceForm } from './lib/useForceForm';
export { default as validate, errorMessages } from './lib/validate';
export { default as unflatList } from './lib/unflatList';
export { default as flatList } from './lib/flatList';
export { default as processFromServer } from './lib/processFromServer';
export { default as getDefaults } from './lib/getDefaults';

const useStyles = createUseStyles(styles, 'ForceForm');

const name = 'ForceForm';

const TYPES = {
    Section,
    ExpandableComponent: InputGroup,
    MultiComponent,
    Text,
    Textarea,
    HtmlTextarea,
    Number: NumberType,
    Integer,
    Checkbox,
    Switch,
    CheckboxGroup,
    BooleanRadioGroup,
    RadioGroup,
    List,
    MultiList,
    Message,
    Date,
    Time,
    Datetime,
    File,
    Static,
    WidgetSize,
    ColorPicker,
    ImagePicker,
    Slider,
    Code,
};

const IS_MULTI_INPUT = [
    'ExpandableComponent',
    'CheckboxGroup',
    'RadioGroup',
    'BooleanRadioGroup',
    'MultiComponent',
];

const STOP_COMPOSITION = [
    'ExpandableComponent',
    'CheckboxGroup',
    'RadioGroup',
    'BooleanRadioGroup',
];

function getValues(values, value, id, type) {
    let newValues = {
        ...values,
        [id]: value,
    };

    if (IS_MULTI_INPUT.includes(type)) {
        newValues = {
            ...values,
            ...value,
        };
    }

    return newValues;
}

function touchField(state, id, value, type) {
    if (IS_MULTI_INPUT.includes(type)) {
        Object.keys(value).forEach((key) => {
            state[key] = {
                isTouched: state.originalValues[key] !== value[key],
                ...state[key],
            };
        });
    } else {
        state[id] = {
            isTouched: state.originalValues[id] !== value,
            ...state[id],
        };
    }
}

function ForceForm({
    className,
    schema,
    onSubmit,
    onChange,
    onState,
    onBlur,
    values = {},
    errors = {},
    mode,
    modifyFieldProps,
    isBulk,
    onBulkCancel,
    onBulkEdit,
    isWeb,
}) {
    const internalState = useRef({ originalValues: values });
    const defaults = useRef(getDefaults(schema));
    const classes = useStyles();
    const { t } = useWeb5Context();
    const requireds = useRef({});
    const isFirst = useRef(true);

    const classNames = classnames(
        classes.root,
        className,
        schema.layout ? `fs-form__${schema.layout.id}` : null,
    );

    useEffect(() => {
        defaults.current = getDefaults(schema);
    }, [schema]);

    const onChangeField = useCallback(
        (value, id, type, mappedFields) => {
            let newValues = values;
            if (mappedFields?.length && type === 'List') {
                const mappedValues = value
                    ? value?.data
                    : mappedFields.reduce((obj, key) => {
                          obj[key] = null;
                          return obj;
                      }, {});
                newValues =
                    mappedFields?.length && type === 'List'
                        ? { ...values, ...mappedValues }
                        : { ...values };
            } else if (type === 'Integer' || type === 'Number') {
                if (value) value = Number(value);
            } else if (type === 'Datetime') {
                if (value) value = value?.toISOString() || value;
            }
            touchField(internalState.current, id, value, type);
            onState && onState(internalState.current);
            onChange && onChange(getValues(newValues, value, id, type), id, value);
        },
        [onChange, onState, values],
    );

    const onBlurField = useCallback(
        (value, id, type, mappedFields) => {
            let newValues = values;
            const finalValue =
                value === undefined ||
                value === '' ||
                value === null ||
                (Array.isArray(value) && !value.length)
                    ? defaults.current[id] || value
                    : value;

            if (mappedFields?.length && type === 'List') {
                const mappedValues = value
                    ? value?.data
                    : mappedFields.reduce((obj, key) => {
                          obj[key] = null;
                          return obj;
                      }, {});
                newValues =
                    mappedFields?.length && type === 'List'
                        ? { ...values, ...mappedValues }
                        : { ...values };
            }

            touchField(internalState.current, id, value);
            onState && onState(internalState.current);
            onBlur && onBlur(getValues(newValues, finalValue, id, type), id, finalValue);
        },
        [onBlur, onState, values],
    );

    const onEnterField = useCallback(
        (value, id, type) => {
            touchField(internalState.current, id, value);
            onState && onState(internalState.current);
            onSubmit && onSubmit(getValues(values, value, id, type), id, value);
        },
        [onState, onSubmit, values],
    );

    const generateFormTree = useCallback(
        (layout, idx) => {
            if (!layout) return null;

            const {
                id,
                schemaId,
                label,
                placeholder,
                emptyPlaceholder,
                activePlaceholder,
                limit,
                maxSize,
                fileTypes,
                helpTip,
                uiType,
                override = {},
                labelForExpand,
                isDisabled,
                isRequired,
                mappedFields,
                labelMode,
                ...props
            } = layout;
            const componentId = layout.schemaId || layout.id;
            const properties = schema.properties?.[componentId] || {};
            let composition = layout.composition;
            let type = uiType;
            if (type) type = type[0].toUpperCase() + type.slice(1);

            // First iteration
            if (idx === undefined) {
                requireds.current = schema.required
                    ? schema.required.reduce((obj, item) => {
                          obj[item] = true;
                          return obj;
                      }, {})
                    : {};
            }
            if (mappedFields?.length && isRequired) {
                requireds.current = { ...requireds.current, [id]: true };
            }

            let fieldProps = {
                Field: TYPES[type],
                key: componentId || `${type}-${idx}`,
                id: componentId,
                type,
                label: label ? t(label) : '',
                labelMode: labelMode || 'horizontal',
                rawLabel: label,
                placeholder: placeholder ? t(placeholder) : '',
                emptyPlaceholder: emptyPlaceholder ? t(emptyPlaceholder) : '',
                rawPlaceholder: placeholder,
                activePlaceholder: activePlaceholder ? t(activePlaceholder) : '',
                limit,
                maxSize,
                fileTypes,
                isFullWidth: true,
                isRequired: requireds.current[componentId],
                isReadOnly: properties.readOnly || isDisabled,
                labelForExpand: labelForExpand,
                schema,
                mappedFields,
                ...props,
            };

            // Overriding props by mode: ["create", "update"]
            if (mode && override && override[mode]) {
                fieldProps = {
                    ...fieldProps,
                    ...(override[mode] || {}),
                };
            }

            const isMultiInput = IS_MULTI_INPUT.includes(type);
            const hasComposition = !STOP_COMPOSITION.includes(type);
            const isInput = (componentId && idx >= 0 && type) || isMultiInput;
            if (isInput) {
                let isSomeChildRequired = false;
                fieldProps.name = componentId;
                fieldProps.hint = helpTip ? t(helpTip) : '';
                fieldProps.className = `fs-form__field__${componentId || idx}`;
                fieldProps.classes = {
                    input: `fs-form__field__${componentId}--input`,
                    clear: `fs-form__field__${componentId}--clear`,
                };
                if (isMultiInput && composition) {
                    fieldProps.inputs = composition.map((item) => {
                        const itemId = item.schemaId || item.id;
                        if (requireds.current[itemId]) isSomeChildRequired = true;
                        return {
                            key: itemId,
                            label: t(item.label),
                            rawLabel: item.label,
                            name: itemId,
                            placeholder: item.placeholder ? t(item.placeholder) : '',
                            emptyPlaceholder: item.emptyPlaceholder ? t(item.emptyPlaceholder) : '',
                            rawPlaceholder: item.placeholder,
                            isRequired: requireds.current[itemId],
                            isReadOnly: schema.properties?.[itemId]?.readOnly || item.isDisabled,
                            isFullWidth: true,
                            labelForExpand: labelForExpand,
                            isHidden: item.isHidden || (item.isHiddenForWeb && isWeb),
                            hint: item.helpTip,
                            info: item?.hint && {
                                title: t(item.hint?.title),
                                body: t(item.hint?.body),
                            },
                        };
                    });
                    composition = hasComposition ? composition : null;
                }
                fieldProps.isRequired = isSomeChildRequired || requireds.current[componentId];
            }

            const children = composition ? composition.map(generateFormTree) : null;
            if (!type) return children;

            // Middleware function for props customization
            fieldProps = modifyFieldProps ? modifyFieldProps(fieldProps, schema) : fieldProps;
            return {
                Node: FieldControl,
                props: fieldProps,
                children,
            };
        },
        [isWeb, mode, modifyFieldProps, schema, t],
    );

    const generateForm = useCallback(
        (formTree) => {
            if (!formTree) return null;
            if (!Array.isArray(formTree)) formTree = [formTree];
            return formTree.map((node, index) => {
                const id =
                    node.props.id ||
                    node.props?.composition?.[0]?.id ||
                    node.props?.composition?.[0]?.schemaId;

                const error = errors[id]
                    ? t(errors[id].message, errors[id].params) || errors[id].message
                    : undefined;
                let value = values?.[id];

                if (node.props.mappedFields?.length && node.props.type === 'List') {
                    node.props.options = node.props.options.map((el) => ({
                        ...el,
                        label: t(el.label),
                    }));
                    value = node.props.options.find((el) =>
                        Object.keys(el.data).reduce(
                            (acc, key) => acc && el.data?.[key] === values?.[key],
                            true,
                        ),
                    );
                    values[id] = value;
                }

                const props = {
                    ...node.props,
                    error,
                    value,
                    onEnter: onEnterField,
                    onChange: onChangeField,
                    onBlur: onBlurField,
                    autoFocus: isFirst.current && node.props.type !== 'Section' && !value,
                    isBulk,
                    bulkEditLabel: t('placeholder_click_to_edit'),
                    bulkCancelLabel: t('action_cancel'),
                    onBulkEdit,
                    onBulkCancel,
                    key: id,
                };

                if (isFirst.current && props.type !== 'Section' && !value) isFirst.current = false;

                if (node.props.inputs) {
                    props.inputs = props.inputs?.filter((el) => !el.isHidden);
                }
                if (node.props.isHidden) return null;

                if (IS_MULTI_INPUT.includes(node.props.type)) props.value = values;

                return (
                    <node.Node {...props} key={id || index}>
                        {node.children ? node.children.map(generateForm) : null}
                    </node.Node>
                );
            });
        },
        [
            errors,
            t,
            values,
            onEnterField,
            onChangeField,
            onBlurField,
            isBulk,
            onBulkEdit,
            onBulkCancel,
        ],
    );

    const formTree = useMemo(() => {
        let form = generateFormTree(schema.layout);
        return form;
    }, [generateFormTree, schema.layout]);

    const form = useMemo(() => generateForm(formTree), [formTree, generateForm]);

    return (
        <form className={classNames} autoComplete="off">
            {form}
        </form>
    );
}

ForceForm.propTypes = {
    className: PropTypes.string,
    values: PropTypes.object,
    errors: PropTypes.object,
    onChange: PropTypes.func,
    onSubmit: PropTypes.func,
    schema: PropTypes.object.isRequired,
    modifyFieldProps: PropTypes.func,
    mode: PropTypes.string,
};

const component = React.memo(ForceForm);

component.displayName = name;

export default component;
